/* eslint-disable no-param-reassign */
import { TextField } from '@mui/material'
import { useCallback } from 'react'
import { isDecimal, noop } from 'common'

export const NumberInput = ({
  onChange = noop,
  showStepper = false,
  allowDecimals = false,
  decimalPlaces = 2,
  allowNegatives = true,
  min = null,
  max = null,
  dataTestId = 'number-input',
  ...props
}) => {
  // Mui number input not triggering onChange for comma and dot
  const handleKeyDown = useCallback(
    e => {
      const blacklist = allowDecimals ? [','] : ['.', ',']
      if (blacklist.includes(e.key)) {
        e.preventDefault()
      }
    },
    [allowDecimals],
  )

  const handleChange = useCallback(
    event => {
      // if value is provided but not a number do nothing
      const { value } = event.target
      const isNegativeChar = allowNegatives ? value === '-' : false
      if (value && !isNegativeChar && !isDecimal(value)) {
        return null
      }

      // if decimal is provided then round to decimalPlaces
      if (!!+value && allowDecimals && decimalPlaces) {
        const decimalPlacesStr = event.target.value.toString().split('.')[1]
        if (decimalPlacesStr?.length > decimalPlaces) {
          return null
        }
      }

      onChange(event)
    },
    [allowDecimals, allowNegatives, decimalPlaces, max, min, onChange],
  )

  const handleBlur = useCallback(
    event => {
      const { value } = event.target
      const hasValue = !!+value

      // if no value, set to 0
      if (!hasValue) {
        event.target.value = 0
      }

      // if negative is not allowed, make it absolute
      if (!allowNegatives) {
        event.target.value = Math.abs(event.target.value)
      }

      // Clamp the value between min and max if present preserving string type
      if (min !== null && min !== undefined && parseFloat(value) < min) {
        event.target.value = Math.max(parseFloat(value), min).toString()
      }
      if (max !== null && max !== undefined && parseFloat(value) > max) {
        event.target.value = Math.min(parseFloat(value), max).toString()
      }

      onChange(event)
    },
    [allowNegatives, onChange],
  )

  const type = showStepper ? 'number' : 'text'

  return (
    <TextField
      type={type}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onBlur={handleBlur}
      data-testid={dataTestId}
      {...props}
    />
  )
}
